import { isDefined, isNonEmpty } from '@sgme/fp';
import classNames from 'classnames';
import { useReadonlyField } from 'components/common-form/contexts/readonly/FieldMetadataContext';
import { SummaryFieldRow } from 'components/common-form/sidePanel/docToolbar/summary/builder/node/SummaryFieldRow';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'components/shared/Intl';
import { CurrencySymbol } from 'components/shared/NewAmountInput/CurrencySymbol';
import { UnderlyingTag } from 'components/underlying-tag';
import type { IntlKey } from 'locales';
import type { UnderlyingCodeType } from 'models/underlying/base';
import type { CommodityUnderlying } from 'models/underlying/commodity';
import type { CreditUnderlying } from 'models/underlying/credit';
import type { ForexUnderlying } from 'models/underlying/forex';
import { RateUnderlying } from 'models/underlying/rate';
import { ComponentProps, FC, useMemo } from 'react';
import { getUnderlyingCode } from 'utils/underlying/underlying-code';

import type { ProductSummaryUnderlyingsFieldProps } from './UnderlyingField.connect';
import { connectProductSummaryUnderlyingField } from './UnderlyingField.connect';

const Table = ({ className, ...props }: ComponentProps<'table'>) => (
  <table {...props} className={classNames('table table-borderless text-center mb-1', className)} />
);

const TableContainer = ({ className, ...props }: ComponentProps<'div'>) => (
  <div {...props} className={classNames('border border-primary px-0', className)} />
);

const EMPTY_DATA = [] as const;
export const Component: FC<ProductSummaryUnderlyingsFieldProps> = ({ node, defaultUnderlyingCodeType }) => {
  const data = useReadonlyField().getMetadataWithData(node) ?? EMPTY_DATA;

  const translationKey = node.translationKey ?? node.id;
  const showWeight = data.some(x => isDefined(x.weight));

  const isEquity = useMemo<boolean>(() => isNonEmpty(data) && data[0].asset.localeCompare('Equity') === 0, [data]);

  const isForex = useMemo<boolean>(() => isNonEmpty(data) && data[0].asset.localeCompare('Forex') === 0, [data]);

  const isRate = useMemo<boolean>(() => isNonEmpty(data) && data[0].asset.localeCompare('Rate') === 0, [data]);

  const hasCutOff = useMemo(() => {
    return isForex && node.hasCutOff;
  }, [isForex, node]);

  const isCreditRateRefEntity = useMemo<boolean>(
    () =>
      isNonEmpty(data) &&
      data[0].asset.localeCompare('CreditRate') === 0 &&
      data[0].type.localeCompare('RefEntity') === 0,
    [data],
  );

  const isCreditRateIndexCredit = useMemo<boolean>(
    () =>
      isNonEmpty(data) &&
      data[0].asset.localeCompare('CreditRate') === 0 &&
      data[0].type.localeCompare('IndexCredit') === 0,
    [data],
  );

  function getUnderlyingLabelTranslationKey(defaultUnderlyingCodeType: UnderlyingCodeType): IntlKey {
    return defaultUnderlyingCodeType === 'Reuters' ? 'reuters' : 'bloomberg';
  }

  return (
    <SummaryFieldRow labelKey={translationKey} id={node.id}>
      {isEquity && data.length > 0 && (
        <TableContainer className="w-75">
          <Table>
            <thead>
              <tr>
                <th data-e2e="underlying-header-code">
                  <FormattedMessage id={getUnderlyingLabelTranslationKey(defaultUnderlyingCodeType)} />
                </th>
                <th data-e2e="underlying-header-strike">
                  <FormattedMessage id="productSummary.underlyingsGrid.strikeMode" />
                </th>
                {showWeight && (
                  <th data-e2e="underlying-header-weight">
                    <FormattedMessage id="productSummary.underlyingsGrid.weight" />
                  </th>
                )}
                <th data-e2e="underlying-header-spot">
                  <FormattedMessage id="productSummary.underlyingsGrid.strikeValue" />
                </th>
                <th data-e2e="underlying-header-valuation">
                  <FormattedMessage id="productSummary.underlyingsGrid.valuation" />
                </th>
              </tr>
            </thead>
            <tbody>
              {(data as CommodityUnderlying[]).map(underlying => (
                <tr key={underlying.id} className="m-0 fw-light" data-e2e="underlying-row">
                  <td data-e2e="code">
                    <UnderlyingTag
                      value={underlying}
                      label={getUnderlyingCode(underlying, defaultUnderlyingCodeType)}
                      isReadOnly
                    />
                  </td>
                  <td data-e2e="strike">
                    {underlying.strike !== undefined && (
                      <FormattedMessage
                        id={`form.StrikeMode.${underlying.strike.strikeMode}` as any}
                        defaultMessage={underlying.strike.strikeMode}
                      />
                    )}
                  </td>
                  {showWeight && (
                    <td data-e2e="weight">
                      <FormattedNumber value={underlying.weight} maximumFractionDigits={3} />
                      {underlying.isWeightInPercent && '%'}
                    </td>
                  )}
                  <td data-e2e="spot" className="text-center">
                    <CurrencySymbol code={underlying.currency} />
                    {underlying.strike?.strikeValue === undefined || underlying.strike?.strikeValue === 0
                      ? ''
                      : ' ' + underlying.strike?.strikeValue}
                  </td>
                  <td data-e2e="valuation">
                    <FormattedDate
                      value={underlying.strike?.strikeDate as string}
                      year="numeric"
                      month="short"
                      day="2-digit"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      )}

      {isCreditRateRefEntity && data.length > 0 && (
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th data-e2e="underlying-header-name">Name</th>
                <th data-e2e="underlying-header-seniority">Seniority</th>
                <th data-e2e="underlying-header-curve-name">Curve name</th>
              </tr>
            </thead>
            <tbody>
              {(data as CreditUnderlying[]).map((underlying: CreditUnderlying) => {
                return (
                  <tr key={underlying.id} className="m-0 fw-light" data-e2e="underlying-row">
                    <td data-e2e="name">{underlying.name}</td>
                    <td data-e2e="seniority">
                      <FormattedMessage
                        id={`quote.underlyingsGrid.seniorityTypes.${underlying.seniorityType}` as any}
                        defaultMessage={underlying.seniorityType}
                      />
                    </td>
                    <td data-e2e="curve-name">{underlying.id}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableContainer>
      )}

      {isCreditRateIndexCredit && data.length > 0 && (
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th data-e2e="underlying-header-name">Name</th>
                <th data-e2e="underlying-header-curve-name">Curve name</th>
              </tr>
            </thead>
            <tbody>
              {(data as CreditUnderlying[]).map((underlying: CreditUnderlying) => {
                return (
                  <tr key={underlying.id} className="m-0 fw-light" data-e2e="underlying-row">
                    <td data-e2e="name">{underlying.name}</td>
                    <td data-e2e="curve-name">{underlying.id}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableContainer>
      )}

      {isForex && data.length > 0 && (
        <>
          {hasCutOff && (
            <TableContainer>
              <Table>
                <thead>
                  <tr>
                    <th data-e2e="underlying-header-name">Name</th>
                    <th data-e2e="underlying-header-cutoff">Cut off</th>
                  </tr>
                </thead>
                <tbody>
                  {(data as ForexUnderlying[]).map((underlying: ForexUnderlying) => {
                    return (
                      <tr key={underlying.id} className="m-0 fw-light" data-e2e="underlying-row">
                        <td data-e2e="name">{underlying.name}</td>
                        <td data-e2e="cutoff">{underlying.cutOff}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableContainer>
          )}
          {!hasCutOff &&
            (data as ForexUnderlying[]).map(underlying => (
              <div key={underlying.id} className="badge badge-outline-secondary badge-lg rounded-pill" data-e2e="name">
                <span className="text-primary">{underlying.name}</span>
              </div>
            ))}
        </>
      )}
      {isRate && (
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th data-e2e="underlying-header-name">Name</th>
              </tr>
            </thead>
            <tbody>
              {(data as RateUnderlying[]).map(underlying => {
                return (
                  <tr key={underlying.id} className="m-0 fw-light" data-e2e="underlying-row">
                    <td data-e2e="name">{underlying.name}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableContainer>
      )}
    </SummaryFieldRow>
  );
};

Component.displayName = 'ProductSummaryUnderlyingsField';

export const ProductSummaryUnderlyingsField = connectProductSummaryUnderlyingField(Component);
